import { create } from 'zustand'
import axios from 'axios'
import type { User } from '../types'

interface AuthState {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (credential: string) => Promise<void>
  loginTest: () => Promise<void>
  logout: () => Promise<void>
  deleteAccount: () => Promise<void>
  checkAuth: () => Promise<void>
  setSubdomain: (subdomain: string) => Promise<void>
  setVisibility: (isPublic: boolean) => Promise<void>
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isLoading: true,
  isAuthenticated: false,

  login: async (credential: string) => {
    try {
      const response = await axios.post('/auth/google', { credential })
      set({ 
        user: response.data.user, 
        isAuthenticated: true,
        isLoading: false 
      })
    } catch (error) {
      console.error('Login failed:', error)
      set({ isLoading: false })
      throw error
    }
  },

  loginTest: async () => {
    try {
      const response = await axios.post('/auth/test-login')
      set({
        user: response.data.user,
        isAuthenticated: true,
        isLoading: false
      })
    } catch (error) {
      console.error('Test login failed:', error)
      set({ isLoading: false })
      throw error
    }
  },

  logout: async () => {
    try {
      await axios.post('/auth/logout')
      set({ user: null, isAuthenticated: false })
    } catch (error) {
      console.error('Logout failed:', error)
    }
  },

  deleteAccount: async () => {
    try {
      await axios.post('/auth/delete-account')
      set({ user: null, isAuthenticated: false })
    } catch (error) {
      console.error('Delete account failed:', error)
      throw error
    }
  },

  checkAuth: async () => {
    try {
      const response = await axios.get('/auth/me')
      const user = response.data.user ?? null
      set({ 
        user,
        isAuthenticated: !!user,
        isLoading: false 
      })
    } catch {
      set({ 
        user: null, 
        isAuthenticated: false,
        isLoading: false 
      })
    }
  },

  setSubdomain: async (subdomain: string) => {
    try {
      await axios.post('/auth/set-subdomain', { subdomain })
      set((state) => ({
        user: state.user ? { ...state.user, subdomain } : null
      }))
    } catch (error) {
      console.error('Failed to set subdomain:', error)
      throw error
    }
  },

  setVisibility: async (isPublic: boolean) => {
    try {
      await axios.post('/api/profile/visibility', { is_public: isPublic })
      set((state) => ({
        user: state.user ? { ...state.user, is_public: isPublic } : null
      }))
    } catch (error) {
      console.error('Failed to set profile visibility:', error)
      throw error
    }
  }
}))
